import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Skeleton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  Query,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  increment,
} from "firebase/firestore";
import { DATABASE, FUNCTIONS } from "../../../../Config/firebase";
import { httpsCallable } from "firebase/functions";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyIcon from "@mui/icons-material/Key";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import TableContainer from "../../../../Components/TableContainer/TableContainer";
import MobileCardView from "../../../../Components/MobileCardView/MobileCardView";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../../Components/useDebounce/useDebounce";
import FundsPageShell from "../../../../Components/FundsPeriodTotals/FundsPageShell";
import FundsPageToolbar from "../../../../Components/FundsPeriodTotals/FundsPageToolbar";
import useFundsPeriodTotals, {
  FUNDS_PERIOD_TYPES,
} from "../../../../hooks/useFundsPeriodTotals";
import {
  filterByFundsPeriod,
  filterByClientSegment,
  getDefaultFundsPeriodStart,
  getDefaultFundsPeriodEnd,
  hasActiveFundsFilters,
  isCapitalRoutingRow,
} from "../../../../Utils/fundsPeriodTotals";
import CapitalRoutingChip from "../../../../Components/CapitalRoutingChip/CapitalRoutingChip";
import PaidIcon from "@mui/icons-material/Paid";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import FullWithdrawModel from "./FullWithdrawModel";
import moment from "moment";
import { getFundRules } from "../../../../Utils/fundRules";
import {
  getWithdrawalWalletDisplay,
  getWithdrawalWalletTooltip,
} from "../../../../Utils/cryptoWalletUtils";

/** Recompute original_balance on open withdrawals after a sibling reject/cancel restores balance. */
const reconcilePendingWithdrawalOriginalBalances = async (userId) => {
  const userSnap = await getDoc(doc(DATABASE, "users", userId));
  if (!userSnap.exists()) return;

  let running = parseFloat(userSnap.data()?.net_balance) || 0;
  const snap = await getDocs(
    query(
      collection(DATABASE, "transection_requests"),
      where("user_id", "==", userId),
      where("request_type", "==", 1),
      where("request_status", "in", [1, 2])
    )
  );

  const pending = snap.docs
    .filter((d) => {
      const x = d.data();
      return x.otp_verify === true && x.balance_deducted === true && x.balance_restored !== true;
    })
    .sort((a, b) => (a.data().date?.seconds || 0) - (b.data().date?.seconds || 0));

  for (let i = pending.length - 1; i >= 0; i--) {
    const d = pending[i];
    const amt = parseFloat(d.data().total_amount) || 0;
    const correctOriginal = running + amt;
    const currentOriginal = parseFloat(d.data().original_balance);
    if (Math.abs(correctOriginal - currentOriginal) > 0.01) {
      await updateDoc(d.ref, { original_balance: correctOriginal });
    }
    running += amt;
  }
};

const WithdrowFunds = () => {
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [withdrowStatus, setWithdrowStatus] = useState(null);
  const [withdrowList, setWithdrowList] = useState([]);
  const [searchWithdraw, setSearchWithdraw] = useState("");
  const [usersMap, setUsersMap] = useState({});
  const debouncedSearch = useDebounce(searchWithdraw, 500);
  const [clientSegment, setClientSegment] = useState(null);
  const [periodStartDate, setPeriodStartDate] = useState(getDefaultFundsPeriodStart);
  const [periodEndDate, setPeriodEndDate] = useState(getDefaultFundsPeriodEnd);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);
  const { totals: periodTotals, loading: periodTotalsLoading } = useFundsPeriodTotals({
    type: FUNDS_PERIOD_TYPES.WITHDRAWAL,
    startDate: periodStartDate,
    endDate: periodEndDate,
    usersMap,
  });

  const filtersActive = hasActiveFundsFilters({
    search: searchWithdraw,
    status: withdrowStatus,
    clientSegment,
    dateFilterActive,
    periodStartDate,
    periodEndDate,
  });

  const resetAllFilters = () => {
    setSearchWithdraw("");
    setWithdrowStatus(null);
    setClientSegment(null);
    setDateFilterActive(false);
    setPeriodStartDate(getDefaultFundsPeriodStart());
    setPeriodEndDate(getDefaultFundsPeriodEnd());
    setDatePickerResetKey((key) => key + 1);
    setPage(0);
  };

  const handleClientSegmentChange = (segment) => {
    setClientSegment(segment);
    setPage(0);
  };

  const handlePeriodStartChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodStartDate(value);
    setPage(0);
    if (periodEndDate && value.isAfter(periodEndDate)) {
      setPeriodEndDate(value);
    }
  };

  const handlePeriodEndChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodEndDate(value);
    setPage(0);
    if (periodStartDate && value.isBefore(periodStartDate)) {
      setPeriodStartDate(value);
    }
  };

  const [isFullWithdrawModel, setIsFullWithdrawModel] = useState(false);
  const [processingWithdrawId, setProcessingWithdrawId] = useState(null);
  const [offerAmountDetails, setOfferAmountDetails] = useState({});
  const [withdrawalThreshold, setWithdrawalThreshold] = useState(80); // Default threshold, will be loaded from fund_rules
  const [bybitStatus, setBybitStatus] = useState({ loading: true, keyCreatedAt: null, daysLeft: null, balance: null, balances: [], exchangeEnabled: false, refreshedAt: null });

  const [bybitSettingsOpen, setBybitSettingsOpen] = useState(false);
  const [bybitSettingsForm, setBybitSettingsForm] = useState({ keyDate: "", exchangeEnabled: false, enforceKyc: false });
  const [bybitSettingsSaving, setBybitSettingsSaving] = useState(false);

  // E15 — System Health: real-time counters by payout state (auto-pay + manual)
  const [health, setHealth] = useState({
    pendingOtp: 0,
    awaitingApproval: 0,
    queued: 0,
    sending: 0,
    awaitingManualPay: 0,
    failed: 0,
    paid24h: 0,
    loading: true,
  });

  const loadBybitStatus = useCallback(async () => {
    setBybitStatus((s) => ({ ...s, loading: true }));
    try {
      const fn = httpsCallable(FUNCTIONS, "getBybitFundBalance");
      const res = await fn();
      setBybitStatus({ loading: false, refreshedAt: new Date(), ...res.data });
      // Seed the form with current values (only on first load, not on refresh)
      if (res.data.keyCreatedAt) {
        setBybitSettingsForm((f) => ({
          keyDate: f.keyDate || res.data.keyCreatedAt.slice(0, 10),
          exchangeEnabled: res.data.exchangeEnabled ?? f.exchangeEnabled,
          enforceKyc: res.data.enforceKyc ?? f.enforceKyc,
        }));
      } else {
        setBybitSettingsForm((f) => ({
          keyDate: f.keyDate,
          exchangeEnabled: res.data.exchangeEnabled ?? f.exchangeEnabled,
          enforceKyc: res.data.enforceKyc ?? f.enforceKyc,
        }));
      }
    } catch (err) {
      setBybitStatus((s) => ({ ...s, loading: false, refreshedAt: new Date(), error: err?.message }));
    }
  }, []);

  const saveBybitSettings = async () => {
    setBybitSettingsSaving(true);
    try {
      // E9 (11/06): block turning Auto-Pay off while a payout is in flight on Bybit.
      // (Backend still confirms in-flight payouts regardless of the toggle, but we
      // stop the founder from disabling it mid-flight to avoid confusion.)
      const turningOff = bybitStatus.exchangeEnabled && !bybitSettingsForm.exchangeEnabled;
      if (turningOff) {
        const inFlightQuery = query(
          collection(DATABASE, "transection_requests"),
          where("request_type", "==", 1),
          where("auto_pay_payout_status", "in", ["submitting", "submitted"]),
          limit(1)
        );
        const inFlightSnap = await getDocs(inFlightQuery);
        if (!inFlightSnap.empty) {
          toast.error(
            "Cannot disable Auto-Pay while a withdrawal is being sent on Bybit. Wait for it to confirm, then try again."
          );
          setBybitSettingsSaving(false);
          return;
        }
      }

      const rulesRef = doc(DATABASE, "fund_rules", "active");
      const updates = {
        "auto_pay.exchange_enabled": bybitSettingsForm.exchangeEnabled,
        "auto_pay.enforce_kyc": bybitSettingsForm.enforceKyc,
      };
      if (bybitSettingsForm.keyDate) {
        updates["bybit_api_key_created_at"] = Timestamp.fromDate(new Date(bybitSettingsForm.keyDate));
      }
      await updateDoc(rulesRef, updates);
      toast.success("Bybit settings saved");
      await loadBybitStatus();
    } catch (err) {
      toast.error("Error saving: " + err.message);
    } finally {
      setBybitSettingsSaving(false);
    }
  };

  useEffect(() => {
    loadBybitStatus();
    const timer = setInterval(loadBybitStatus, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(timer);
  }, [loadBybitStatus]);

  // Detect mobile device (breakpoint at 960px)
  const isMobile = useMediaQuery("(max-width: 960px)");
  const withdrowStatusList = [
    {
      id: null,
      name: "All",
    },
    {
      id: 0,
      name: "Pending",
    },
    {
      id: 2,
      name: "Mark As Paid",
    },
    {
      id: 3,
      name: "Cancelled - By User",
    },
    {
      id: 4,
      name: "Rejected",
    },
    {
      id: 5,
      name: "Paid",
    },
  ];
  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (ts?.toDate && typeof ts.toDate === "function") return moment(ts.toDate());
    const seconds = ts?.seconds;
    const nanoseconds = ts?.nanoseconds;
    if (seconds == null) return null;
    const milliseconds = seconds * 1000 + Math.floor((nanoseconds || 0) / 1000000);
    return moment(milliseconds);
  };

  const cellEllipsisSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
  };

  const renderCompactDateTime = (ts) => {
    const dateP = parseTimestamp(ts);
    if (!dateP) {
      return (
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
          --
        </Typography>
      );
    }
    return (
      <Box sx={{ lineHeight: 1.3, textAlign: "center", minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
          {dateP.format("DD/MM/YY")}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 13, lineHeight: 1.2 }}>
          {dateP.format("HH:mm")}
        </Typography>
      </Box>
    );
  };

  /** Paid, rejected, or cancelled — only then show Fin. date (not Req. / approval time). */
  const getFinishedTimestamp = (row) => {
    const status = row?.request_status;
    if (status !== 3 && status !== 4 && status !== 5) return null;
    return row?.conclusion_date || row?.request_approval_time || null;
  };

  const formatUsd = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value));
  };

  const getWithdrawalDeductionAmount = (row) => {
    const offered = parseFloat(row?.offered_amount);
    if (!Number.isNaN(offered) && offered > 0) return offered;
    return parseFloat(row?.total_amount) || 0;
  };

  /** Balance when the client submitted the withdrawal (original_balance snapshot). */
  const getPreviousBalance = (row) => {
    const ob = parseFloat(row?.original_balance);
    if (!Number.isNaN(ob) && ob >= 0) return ob;
    const nb = parseFloat(row?.net_balance);
    const amt = getWithdrawalDeductionAmount(row);
    if (!Number.isNaN(nb) && amt >= 0) return nb + amt;
    return null;
  };

  /** Auto-Pay owns this accepted withdrawal — block manual reject/paid until failed or paid. */
  const isAutoPayManaged = (row) => {
    if (Number(row?.request_status) !== 2) return false;
    if (row?.auto_pay_triggered !== true) return false;
    const ps = String(row?.auto_pay_payout_status || "");
    if (ps === "failed") return false;
    return true;
  };

  /** E10: block explorer base URL by Fiddux network label (default Polygon). */
  const EXPLORER_TX_BASE = {
    POLYGON: "https://polygonscan.com/tx/",
    ETHEREUM: "https://etherscan.io/tx/",
    BSC: "https://bscscan.com/tx/",
  };

  /** E10: on-chain explorer URL for an auto-pay payout, or null if no tx hash. */
  const getExplorerTxUrl = (row) => {
    const txHash = String(row?.auto_pay_tx_hash || "").trim();
    if (!txHash) return null;
    const network = String(row?.selected_wallet_network || "POLYGON").toUpperCase();
    const base = EXPLORER_TX_BASE[network] || EXPLORER_TX_BASE.POLYGON;
    return `${base}${txHash}`;
  };

  const renderBybitBalanceChip = (entry, { compact = false } = {}) => {
    if (!entry) return null;
    const coin = entry.coin || "USDT";
    const chipSx = compact
      ? { height: 22, fontSize: "0.7rem", "& .MuiChip-label": { px: 0.75 } }
      : { height: 24 };
    const iconSx = compact ? { fontSize: 12 } : { fontSize: 14 };
    if (entry.error) {
      const err = entry.error || "";
      const isPermissionError = /Permission denied|10005|permissions/i.test(err);
      return (
        <Tooltip
          key={coin}
          title={
            isPermissionError
              ? `Bybit API key has no balance-read permission for ${coin}. Check the FUND wallet on bybit.com.`
              : err
          }
        >
          <Chip
            icon={<AccountBalanceWalletIcon sx={iconSx} />}
            label={isPermissionError ? `${coin}: see on bybit.com` : `${coin}: error`}
            size="small"
            color={isPermissionError ? "default" : "error"}
            variant="outlined"
            onClick={isPermissionError ? () => window.open("https://www.bybit.com/user/assets/home/account", "_blank") : undefined}
            sx={{ ...chipSx, cursor: isPermissionError ? "pointer" : "default" }}
          />
        </Tooltip>
      );
    }
    return (
      <Tooltip
        key={coin}
        title={`Bybit FUND wallet · ${coin} · Auto-Pay ${bybitStatus.exchangeEnabled ? "ON" : "OFF"}`}
      >
        <Chip
          icon={<AccountBalanceWalletIcon sx={iconSx} />}
          label={`${coin} $${parseFloat(entry.walletBalance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          size="small"
          variant="outlined"
          sx={{
            ...chipSx,
            opacity: bybitStatus.exchangeEnabled ? 1 : 0.7,
          }}
        />
      </Tooltip>
    );
  };

  const bybitBalanceEntries = (() => {
    if (Array.isArray(bybitStatus.balances) && bybitStatus.balances.length > 0) {
      return bybitStatus.balances;
    }
    if (bybitStatus.balance) return [bybitStatus.balance];
    return [];
  })();

  /** Bybit submit/poll in progress — show ⏳ Bybit badge. */
  const isBybitInFlight = (row) => {
    const ps = row?.auto_pay_payout_status;
    return ps === "submitting" || ps === "submitted";
  };

  /** Balance after finalization (paid / cancelled / rejected). */
  const getNewBalanceAfterFinalization = (row) => {
    const status = row?.request_status;
    if (status !== 3 && status !== 4 && status !== 5) return null;

    const prev = getPreviousBalance(row);
    if (prev == null) return null;
    const amt = getWithdrawalDeductionAmount(row);

    if (status === 5) return prev - amt;
    if (status === 3 || status === 4) return prev;
    return null;
  };

  const renderBalanceCell = (value, tooltipNote) => {
    const formatted = formatUsd(value);
    if (!formatted) {
      return (
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
            --
          </Typography>
        </Box>
      );
    }
    return (
      <Tooltip title={tooltipNote ? `${formatted} — ${tooltipNote}` : formatted}>
        <Box sx={{ width: "100%", textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatted}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: "requested_at",
      headerName: "Req.",
      width: 84,
      sortable: false,
      renderCell: (params) => renderCompactDateTime(params?.row?.date),
    },
    {
      field: "finished_at",
      headerName: "Fin.",
      width: 84,
      hideOnMobile: true,
      hideOnTablet: true,
      sortable: false,
      renderCell: (params) => {
        const ts = getFinishedTimestamp(params?.row);
        if (!ts) {
          return (
            <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 14 }}>
              --
            </Typography>
          );
        }
        return renderCompactDateTime(ts);
      },
    },
    {
      field: "user_name",
      headerName: "Client",
      width: 200,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const name = params?.row?.user_name || "--";
        const email = params?.row?.email || "";
        return (
          <Tooltip title={email ? `${name}\n${email}` : name}>
            <Box sx={{ lineHeight: 1.35, minWidth: 0, width: "100%", textAlign: "left", py: 0.25 }}>
              <Typography variant="body2" sx={{ ...cellEllipsisSx, fontWeight: 600, fontSize: 14, color: "text.primary" }}>
                {name}
              </Typography>
              {email ? (
                <Typography variant="caption" sx={{ ...cellEllipsisSx, fontSize: 13, color: "text.secondary", display: "block" }}>
                  {email}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "total_amount",
      headerName: "Amount",
      width: 150,
      renderCell: (params) => {
        const amount = getWithdrawalDeductionAmount(params.row);
        const is_full_withdraw = params?.row?.is_full_withdraw;
        const label = formatUsd(amount) || "--";
        return (
          <Tooltip title={is_full_withdraw ? `${label} — Full withdraw` : label}>
            <Box sx={{ lineHeight: 1.3, textAlign: "center", width: "100%" }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {label}
              </Typography>
              {is_full_withdraw && (
                <Chip label="Full" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 12, mt: 0.25 }} />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "selected_wallet",
      headerName: "Wallet",
      width: 128,
      hideOnMobile: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        if (isCapitalRoutingRow(params?.row)) {
          return (
            <Tooltip title="Capital Routing — internal transfer to another Fiddux account (no on-chain payout).">
              <Chip
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                label="Internal routing"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Tooltip>
          );
        }
        if (params?.row?.destination_type === "fiat_manual") {
          return (
            <Tooltip title="Paid in FIAT manually by Fiddux — process outside crypto (no on-chain payout).">
              <Chip
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                label="FIAT manual"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={getWithdrawalWalletTooltip(params.row)}>
            <Typography variant="body2" sx={{ ...cellEllipsisSx, fontSize: 14, color: "text.primary" }}>
              {getWithdrawalWalletDisplay(params.row)}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 110,
      filterable: false,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const notes = params?.row?.notes || "";
        const display = notes || "--";
        return (
          <Tooltip title={display}>
            <Typography
              variant="body2"
              sx={{
                ...cellEllipsisSx,
                fontSize: 14,
                color: display === "--" ? "text.secondary" : "text.primary",
              }}
            >
              {display}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "txn_id",
      headerName: "TXN",
      width: 124,
      hideOnMobile: true,
      renderCell: (params) => {
        const txn = params?.row?.txn_id;
        const display = txn != null && txn !== "" ? String(txn) : "--";
        return (
          <Tooltip title={display}>
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "monospace",
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              {display}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "request_status",
      headerName: "Status",
      width: 132,
      minWidth: 132,
      renderCell: (params) => {
        const { request_status } = params?.row;
        const { profit_loss_percent } = params?.row;
        const isProcessing = processingWithdrawId === params?.row?.doc_id;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: "4px",
              flexWrap: "nowrap",
              position: "relative",
            }}
          >
            {request_status === 0 ? (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>⏳ OTP Pending</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Client submitted the withdrawal. Waiting for them to confirm via OTP in the app.
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#ff9800" }}>
                      Next: client confirms OTP → status moves to admin review
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="OTP Pending" sx={{ color: "#fff", backgroundColor: "orange", cursor: "default" }} />
              </Tooltip>
            ) : request_status === 1 && isCapitalRoutingRow(params?.row) ? (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>🔄 Routing in transit</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Sender already debited. Recipient is credited automatically at settlement. Manage it in Capital Routing (Settle now / Cancel).
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="In transit" sx={{ color: "#fff", backgroundColor: "#0288D1", cursor: "default" }} />
              </Tooltip>
            ) : request_status === 1 && profit_loss_percent !== 0 ? (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>⚠️ Awaiting — Performance P&L Active</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Withdrawal confirmed by client but a performance period is active (P&L ≠ 0). This must be resolved before processing.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Awaiting" sx={{ color: "#fff", backgroundColor: "#F44336", cursor: "default" }} />
              </Tooltip>
            ) : request_status === 1 && params?.row?.auto_pay_queued === true ? (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>🕐 Auto-Pay Queue</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Withdrawal in the queue. It will be processed automatically after the client's previous request is finalized.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="🕐 Queued" sx={{ color: "#fff", backgroundColor: "#6A1B9A", cursor: "default", fontWeight: 700 }} />
              </Tooltip>
            ) : request_status === 1 && profit_loss_percent === 0 ? (
              <>
                {(() => {
                  // Check if withdrawal exceeds threshold from fund_rules
                  // Use original_balance if available, otherwise use current net_balance
                  const withdrawalAmount = parseFloat(params?.row?.total_amount) || 0;
                  const originalBalance = parseFloat(params?.row?.original_balance) || 0;
                  const currentBalance = parseFloat(params?.row?.net_balance) || 0;
                  const balanceToUse = originalBalance > 0 ? originalBalance : currentBalance;
                  const threshold = withdrawalThreshold; // Loaded from fund_rules
                  const percentage = balanceToUse > 0 ? (withdrawalAmount / balanceToUse) * 100 : 0;
                  const exceedsThreshold = percentage >= threshold;
                  const conditionalOfferSent = params?.row?.conditional_offer_sent === true;
                  const isFullWithdraw = params?.row?.is_full_withdraw === true;
                  const thresholdTriggered = params?.row?.threshold_triggered === true;
                  
                  // Debug logging
                  if (withdrawalAmount > 0 && balanceToUse > 0) {
                    console.log(`🔍 [Withdrawal ${params?.row?.doc_id}] Threshold check:`, {
                      withdrawalAmount,
                      originalBalance,
                      currentBalance,
                      balanceToUse,
                      threshold,
                      percentage: percentage.toFixed(2),
                      exceedsThreshold,
                      thresholdTriggered,
                      conditionalOfferSent
                    });
                  }
                  
                  // Show "Send Conditional Offer" button if:
                  // 1. Withdrawal exceeds threshold (calculated OR marked by Cloud Function) AND
                  // 2. Conditional offer not yet sent (this handles both new withdrawals and legacy auto-marked ones)
                  // Note: We check conditional_offer_sent, not is_full_withdraw, because legacy withdrawals
                  // may have is_full_withdraw=true from old auto-logic, but conditional_offer_sent will be false
                  // Also check threshold_triggered as fallback (set by Cloud Function)
                  if ((exceedsThreshold || thresholdTriggered) && !conditionalOfferSent) {
                    return (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>💰 Send Conditional Offer</Typography>
                            <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                              Withdrawal exceeds the threshold. Send a conditional offer to the client with an adjusted amount before processing.
                            </Typography>
                          </Box>
                        }
                        arrow placement="left"
                        componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                      >
                        <IconButton
                          sx={{ background: "#FF9800", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "#F57C00" } }}
                          onClick={() => handleAcceptRejectOfferAmount(params?.row)}
                        >
                          <PaidIcon sx={{ color: "#fff", fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                    );
                  }
                  
                  return (
                    <>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>✅ Accept Withdrawal</Typography>
                            <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                              Accept this withdrawal request. Moves to "Ready to Pay" queue for on-chain payment.
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#4caf50" }}>
                              Status → Accepted · Auto-pay engine will process after pay_delay
                            </Typography>
                          </Box>
                        }
                        arrow placement="left"
                        componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                      >
                        <IconButton
                          disabled={isProcessing}
                          sx={{ background: "#4caf50", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "#388e3c" } }}
                          onClick={() => {
                            if (params?.row?.is_full_withdraw) {
                              handleAcceptRejectOfferAmount(params?.row);
                            } else {
                              handleAcceptRejectOnChange(params?.row, 2);
                            }
                          }}
                        >
                          {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CheckIcon sx={{ color: "#fff" }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>❌ Reject Withdrawal</Typography>
                            <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                              Reject this request. The client's balance will be restored. Use if the request cannot be processed.
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#f44336" }}>
                              Status → Rejected · Balance restored · Client notified
                            </Typography>
                          </Box>
                        }
                        arrow placement="right"
                        componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                      >
                        <IconButton
                          disabled={isProcessing}
                          sx={{ background: "red", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "darkred" } }}
                          onClick={() => handleAcceptRejectOnChange(params?.row, 4)}
                        >
                          {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CloseIcon sx={{ color: "#fff" }} />}
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                })()}
              </>
            ) : (
              ""
            )}
            {request_status === 2 && isAutoPayManaged(params?.row) && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {isBybitInFlight(params?.row) ? "⏳ Bybit em andamento" : "⏳ Auto-Pay em andamento"}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      {isBybitInFlight(params?.row)
                        ? "Auto-Pay enviou este saque à Bybit. Rejeitar ou marcar como pago manualmente está bloqueado até a confirmação on-chain."
                        : "Auto-Pay aceitou este saque e vai enviar à Bybit após o pay delay. Rejeitar ou marcar como pago manualmente está bloqueado."}
                    </Typography>
                    {params?.row?.auto_pay_withdrawal_id && (
                      <Typography variant="caption" sx={{ display: "block", mt: 0.75, fontFamily: "monospace" }}>
                        Bybit ID: {params.row.auto_pay_withdrawal_id}
                      </Typography>
                    )}
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 300, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip
                  size="small"
                  label={isBybitInFlight(params?.row) ? "⏳ Bybit" : "⏳ Auto-Pay"}
                  sx={{ color: "#fff", backgroundColor: "#1565C0", cursor: "default", fontWeight: 700 }}
                />
              </Tooltip>
            )}
            {request_status === 2 && !isAutoPayManaged(params?.row) && (
              <>
                {params?.row?.auto_pay_payout_status === "failed" && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>Bybit payout failed</Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                          {params?.row?.auto_pay_payout_error ||
                            "Auto-pay could not send via Bybit (insufficient balance or API error)."}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#ffb74d" }}>
                          Mark as Paid after manual transfer, or Reject to restore the client balance.
                        </Typography>
                      </Box>
                    }
                    arrow placement="left"
                    componentsProps={{ tooltip: { sx: { maxWidth: 340, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        background: "#E65100",
                        width: "30px",
                        height: "30px",
                        borderRadius: "4px",
                        flexShrink: 0,
                        "&:hover": { background: "#BF360C" },
                      }}
                    >
                      <WarningAmberIcon sx={{ color: "#fff", fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>💸 Mark as Paid</Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                        Confirm that the on-chain transfer has been executed. Deducts the amount from client's balance.
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#4caf50" }}>
                        Status → Paid · Balance deducted · Client notified
                      </Typography>
                    </Box>
                  }
                  arrow placement="left"
                  componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                >
                  <IconButton
                    disabled={isProcessing}
                    sx={{ background: "#4caf50", width: "30px", height: "30px", borderRadius: "4px", "&:hover": { background: "#388e3c" } }}
                    onClick={() => handleAcceptRejectOnChange(params?.row, 5)}
                  >
                    {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PaidIcon sx={{ color: "#fff" }} />}
                  </IconButton>
                </Tooltip>
                {!params?.row?.offered_amount && !params?.row?.is_full_withdraw && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>❌ Cancel Accepted Withdrawal</Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                          Cancel this accepted withdrawal before payment. The client's balance will be restored.
                        </Typography>
                      </Box>
                    }
                    arrow placement="right"
                    componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                  >
                    <IconButton
                      disabled={isProcessing}
                      sx={{ background: "red", width: "30px", height: "30px", borderRadius: "4px", ml: "4px", "&:hover": { background: "#b71c1c" } }}
                      onClick={() => handleAcceptRejectOnChange(params?.row, 4)}
                    >
                      {isProcessing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <CloseIcon sx={{ color: "#fff" }} />}
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {request_status === 3 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>🚫 Cancelled</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Withdrawal was cancelled. Client balance restored. No further action available.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 260, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Cancelled" sx={{ color: "#fff", backgroundColor: "#F44336", cursor: "default" }} />
              </Tooltip>
            )}
            {request_status === 4 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>❌ Rejected</Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                      Withdrawal was rejected by admin. Client balance restored. No further action available.
                    </Typography>
                  </Box>
                }
                arrow placement="left"
                componentsProps={{ tooltip: { sx: { maxWidth: 260, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
              >
                <Chip size="small" label="Rejected" sx={{ color: "#fff", backgroundColor: "#D32F2F", cursor: "default" }} />
              </Tooltip>
            )}
            {request_status === 5 && (
              <>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>✅ Payment Completed</Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)" }}>
                        On-chain transfer executed. Amount deducted from client's balance. No further action required.
                      </Typography>
                      {params?.row?.auto_pay_withdrawal_id && (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.75, fontFamily: "monospace", color: "rgba(255,255,255,0.6)" }}>
                          Bybit ID: {params.row.auto_pay_withdrawal_id}
                        </Typography>
                      )}
                      {getExplorerTxUrl(params?.row) && (
                        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#4caf50" }}>
                          Click the link icon to view the transaction on-chain
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow placement="left"
                  componentsProps={{ tooltip: { sx: { maxWidth: 280, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                >
                  <Chip size="small" label="Paid" sx={{ background: "#4caf50", color: "#fff" }} />
                </Tooltip>
                {getExplorerTxUrl(params?.row) && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>🔗 View on explorer</Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "rgba(255,255,255,0.75)", fontFamily: "monospace", wordBreak: "break-all" }}>
                          {params?.row?.auto_pay_tx_hash}
                        </Typography>
                      </Box>
                    }
                    arrow placement="right"
                    componentsProps={{ tooltip: { sx: { maxWidth: 300, p: 1.5, bgcolor: "#1a1a2e", border: "1px solid rgba(255,255,255,0.15)" } } }}
                  >
                    <IconButton
                      size="small"
                      component="a"
                      href={getExplorerTxUrl(params?.row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ p: 0.25, flexShrink: 0, color: "#4caf50", "&:hover": { color: "#2e7d32" } }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {isCapitalRoutingRow(params?.row) && (
              <CapitalRoutingChip routingId={params?.row?.capital_routing_id} />
            )}
          </Box>
        );
      },
    },
    {
      field: "previous_balance",
      headerName: "Prev. Balance",
      flex: 1,
      minWidth: 118,
      hideOnMobile: true,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) =>
        renderBalanceCell(
          getPreviousBalance(params.row),
          "Balance when withdrawal was requested"
        ),
    },
    {
      field: "new_balance",
      headerName: "New Balance",
      flex: 1,
      minWidth: 118,
      hideOnMobile: true,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const value = getNewBalanceAfterFinalization(params.row);
        const status = params.row?.request_status;
        const note =
          status === 5
            ? "After payment (deducted)"
            : status === 3 || status === 4
            ? "After cancel/reject (restored)"
            : "";
        return renderBalanceCell(value, note);
      },
    },
  ];

  // Load withdrawal threshold from fund_rules with real-time listener
  useEffect(() => {
    // Set up real-time listener for fund_rules to update threshold automatically
    const fundRulesRef = doc(DATABASE, 'fund_rules', 'active');
    const unsubscribe = onSnapshot(
      fundRulesRef,
      (docSnap) => {
        try {
          if (docSnap.exists()) {
            const fundRules = docSnap.data();
            const threshold = fundRules?.withdrawal_rules?.large_withdrawal_threshold || 80;
            setWithdrawalThreshold(threshold);
            console.log(`✅ Withdrawal threshold updated to ${threshold}% from fund_rules`);
          } else {
            // Document doesn't exist, use default
            setWithdrawalThreshold(80);
            console.log('⚠️ Fund rules document not found, using default threshold 80%');
          }
        } catch (error) {
          console.error("Error loading withdrawal threshold from fund_rules:", error);
          // Keep default value of 80 if error occurs
          setWithdrawalThreshold(80);
        }
      },
      (error) => {
        console.error("Error setting up fund_rules listener:", error);
        // Fallback to async load if listener fails
        const loadWithdrawalThreshold = async () => {
          try {
            const fundRules = await getFundRules();
            const threshold = fundRules?.withdrawal_rules?.large_withdrawal_threshold || 80;
            setWithdrawalThreshold(threshold);
          } catch (err) {
            console.error("Error loading withdrawal threshold from fund_rules:", err);
            setWithdrawalThreshold(80);
          }
        };
        loadWithdrawalThreshold();
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  // Load users once on mount — avoid re-fetching on every withdrawal snapshot
  useEffect(() => {
    getDocs(collection(DATABASE, "users")).then((snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = {
          doc_id: d.id,
          email: d.data()?.email,
          name: d.data()?.name || d.data()?.full_name,
          net_balance: d.data()?.net_balance,
          ...d.data(),
        };
      });
      setUsersMap(map);
    }).catch((err) => console.error("WithdrowFunds: failed to load users", err));
  }, []);

  useEffect(() => {
    const unsubscribe = getWithdrow(
      page,
      pageSize,
      withdrowStatus,
      debouncedSearch,
      dateFilterActive ? periodStartDate : null,
      dateFilterActive ? periodEndDate : null,
      clientSegment
    );
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, withdrowStatus, debouncedSearch, usersMap, periodStartDate, periodEndDate, dateFilterActive, clientSegment]);

  // E15 — System Health: count withdrawals by payout state in real time.
  // Independent of the table filter so the strip always reflects the whole pipeline.
  useEffect(() => {
    const q = query(
      collection(DATABASE, "transection_requests"),
      where("request_type", "==", 1),
      where("otp_verify", "==", true)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const dayMs = 24 * 60 * 60 * 1000;
        const c = {
          pendingOtp: 0,
          awaitingApproval: 0,
          queued: 0,
          sending: 0,
          awaitingManualPay: 0,
          failed: 0,
          paid24h: 0,
        };
        snap.forEach((d) => {
          const r = d.data();
          if (isCapitalRoutingRow(r)) return;
          const st = Number(r?.request_status);
          const ps = String(r?.auto_pay_payout_status || "");
          if (st === 0) {
            c.pendingOtp += 1;
          } else if (st === 1) {
            if (r?.auto_pay_queued === true) c.queued += 1;
            else c.awaitingApproval += 1;
          } else if (st === 2) {
            if (ps === "failed") c.failed += 1;
            else if (ps === "submitting" || ps === "submitted" || r?.auto_pay_triggered === true) c.sending += 1;
            else c.awaitingManualPay += 1;
          } else if (st === 5) {
            const ts = r?.conclusion_date || r?.request_approval_time;
            const ms = ts?.seconds != null
              ? ts.seconds * 1000
              : ts?.toDate
                ? ts.toDate().getTime()
                : null;
            if (ms && now - ms <= dayMs) c.paid24h += 1;
          }
        });
        setHealth({ ...c, loading: false });
      },
      (err) => {
        console.error("E15 health listener error:", err);
        setHealth((h) => ({ ...h, loading: false }));
      }
    );
    return () => unsub();
  }, []);
  const getWithdrow = (
    currentPage = 0,
    pageSize = 50,
    withdrawStatus = null,
    debouncedSearch = "",
    periodStart = periodStartDate,
    periodEnd = periodEndDate,
    segmentFilter = clientSegment
  ) => {
    setIsLoading(true);
    let q = query(
      collection(DATABASE, "transection_requests"),
      where("request_type", "==", 1),
      where("otp_verify", "==", true),
      orderBy("date", "desc")
    );
    if (withdrawStatus !== null) {
      q = query(q, where("request_status", "==", withdrawStatus));
    }
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const transactions = querySnapshot?.docs?.map((doc, index) => ({
          rowid: index + 1,
          id: doc?.id,
          doc_id: doc?.id,
          ...doc?.data(),
        }));
        const transactionsWithUserNames = transactions.map((transaction) => {
          const matchedUser = usersMap[transaction?.user_id];
          return {
            ...transaction,
            withdraw_approved_by: matchedUser?.name,
            user_name: matchedUser?.name || "Unknown",
            email: matchedUser?.email || "--",
            net_balance: matchedUser?.net_balance || 0,
            user_type: matchedUser?.user_type,
          };
        });
        const periodFiltered = filterByFundsPeriod(
          transactionsWithUserNames,
          periodStart,
          periodEnd,
          1
        );
        const segmentFiltered = filterByClientSegment(periodFiltered, segmentFilter, usersMap);
        const filteredTransactions = debouncedSearch
          ? segmentFiltered?.filter(
              (transaction) =>
                transaction?.email
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase()) ||
                transaction?.user_name
                  .toLowerCase()
                  .includes(debouncedSearch.toLowerCase())
            )
          : segmentFiltered;

        const startIndex = currentPage * pageSize;
        const paginatedTransactions = filteredTransactions?.slice(
          startIndex,
          startIndex + pageSize
        );
        setWithdrowList(paginatedTransactions);
        setTotalCount(filteredTransactions.length);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching withdrawals:", error);
        setIsLoading(false);
      }
    );
    return unsubscribe;
  };

  const getTimestamp = () => {
    const currentDate = moment.utc();
    const hour = currentDate.hour();

    if (hour >= 21) {
      currentDate.add(1, "day").startOf("day");
    }

    return currentDate.toDate();
  };

  const escapeConfirmHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const markPaidConfirmHtml = (row) => {
    const name = row?.user_name || "Unknown";
    const email = row?.email && row.email !== "--" ? row.email : "";
    const amount = formatUsd(getWithdrawalDeductionAmount(row)) || "--";
    const requested = parseTimestamp(row?.date);
    const requestedLabel = requested ? requested.format("DD/MM/YYYY HH:mm") : "--";
    let destination = "--";
    let address = "";
    if (isCapitalRoutingRow(row)) {
      destination = "Internal routing";
    } else if (row?.destination_type === "fiat_manual") {
      destination = "FIAT manual";
    } else {
      const label = String(row?.selected_wallet_label || "").trim();
      const asset = String(row?.selected_wallet_asset || "").trim();
      const network = String(row?.selected_wallet_network || "").trim();
      destination = [label, asset, network].filter(Boolean).join(" · ") || getWithdrawalWalletDisplay(row);
      address = String(row?.selected_wallet_address || "").trim();
    }
    const rowHtml = (label, value, extraStyle = "") =>
      `<tr>
        <td style="padding:6px 12px 6px 0;color:#666;white-space:nowrap;vertical-align:top;">${label}</td>
        <td style="padding:6px 0;vertical-align:top;${extraStyle}">${value}</td>
      </tr>`;
    return `<div style="text-align:left;font-size:14px;line-height:1.45;">
      <p style="margin:0 0 12px;text-align:center;">Confirm that this withdrawal has been paid.</p>
      <table style="width:100%;border-collapse:collapse;">
        ${rowHtml("Client", `<strong>${escapeConfirmHtml(name)}</strong>`)}
        ${email ? rowHtml("Email", escapeConfirmHtml(email)) : ""}
        ${rowHtml("Amount", `<strong>${escapeConfirmHtml(amount)}</strong>${row?.is_full_withdraw ? " · Full" : ""}`)}
        ${rowHtml("Destination", escapeConfirmHtml(destination))}
        ${address ? rowHtml("Address", escapeConfirmHtml(address), "font-family:monospace;font-size:12px;word-break:break-all;") : ""}
        ${rowHtml("Requested", escapeConfirmHtml(requestedLabel))}
      </table>
    </div>`;
  };

  const handleAcceptRejectOnChange = async (Data, Status) => {
    const actionText =
      Status === 2
        ? "approve"
        : Status === 4
        ? "reject"
        : Status === 5
        ? "Mark As Paid"
        : "";
    const result = await Swal.fire(
      Status === 5
        ? {
            title: "Mark as paid?",
            html: markPaidConfirmHtml(Data),
            icon: "warning",
            width: 520,
            showCancelButton: true,
            confirmButtonColor: "#46adf2",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
          }
        : {
            title: "Are you sure?",
            text: `You want to ${actionText} this withdraw`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#46adf2",
            cancelButtonColor: "#5b5d5d",
            confirmButtonText: "Yes",
            cancelButtonText: "Cancel",
          }
    );

    if (!result.isConfirmed) {
      return;
    }

    setIsLoading(true);
    const rowKey = Data?.doc_id || Data?.id;
      setProcessingWithdrawId(rowKey);
      try {
        if (!Data?.user_id) {
          throw new Error("User ID is missing");
        }

        if (Status === 4 || Status === 5) {
          const liveTxnRef = doc(DATABASE, "transection_requests", Data?.doc_id);
          const liveTxnDoc = await getDoc(liveTxnRef);
          const liveData = liveTxnDoc.data() || {};
          if (isAutoPayManaged({ ...Data, ...liveData })) {
            await Swal.fire({
              title: "Ação bloqueada",
              text: isBybitInFlight(liveData)
                ? "Este saque está sendo processado automaticamente pela Bybit. Aguarde a confirmação on-chain antes de rejeitar ou marcar como pago."
                : "Este saque foi aceito pelo Auto-Pay e será enviado à Bybit em breve. Aguarde a conclusão automática antes de rejeitar ou marcar como pago.",
              icon: "error",
            });
            return;
          }
        }
        let amountToDeduct;
        // RESTORED: Balance update logic handled by Admin Panel (reverted from Cloud Function)
        
        if (Status === 4) {
          // Rejection - balance should be restored if it was deducted
          // This handles the case where balance was deducted when OTP was verified
          const txnRef = doc(DATABASE, "transection_requests", Data?.doc_id);
          const txnDoc = await getDoc(txnRef);
          const txnData = txnDoc.data();
          
          // Debug logging
          console.log(`🔍 [REJECTION] Transaction ${Data?.doc_id}:`, {
            otp_verify: txnData?.otp_verify,
            balance_deducted: txnData?.balance_deducted,
            balance_restored: txnData?.balance_restored,
            is_full_withdraw: txnData?.is_full_withdraw,
            conditional_offer_sent: txnData?.conditional_offer_sent,
            total_amount: Data?.total_amount
          });
          
          // Restore balance if OTP was verified (balance was definitely deducted)
          // For conditional withdrawals, balance is still deducted at OTP verification
          const shouldRestore = txnData?.otp_verify === true;
          
          if (shouldRestore && txnData?.balance_restored !== true) {
            const amountToRestore = parseFloat(Data?.total_amount) || 0;
            await runTransaction(DATABASE, async (transaction) => {
              const txnSnap = await transaction.get(txnRef);
              const liveTxnData = txnSnap.data();
              if (!txnSnap.exists()) {
                throw new Error("Transaction does not exist");
              }

              if (liveTxnData?.balance_restored === true) {
                console.log(`⚠️ [REJECTION] Transaction ${Data?.doc_id} already restored, skipping duplicate restoration.`);
                return;
              }

              if (liveTxnData?.otp_verify !== true) {
                console.log(`ℹ️ [REJECTION] OTP not verified for ${Data?.doc_id}. No balance restore required.`);
                return;
              }

              const userRef = doc(DATABASE, "users", Data?.user_id);
              const userSnap = await transaction.get(userRef);
              if (!userSnap.exists()) {
                throw new Error("User does not exist");
              }

              const netBeforeRestore = parseFloat(userSnap.data()?.net_balance) || 0;
              const correctOriginal = netBeforeRestore + amountToRestore;

              transaction.update(userRef, {
                net_balance: increment(amountToRestore),
                _last_balance_sync: serverTimestamp(),
              });
              transaction.update(txnRef, {
                original_balance: correctOriginal,
                balance_restored: true,
              });
            });

            await reconcilePendingWithdrawalOriginalBalances(Data?.user_id);

            // Update ledger entry: set current_balance back to previous_balance
            // This prevents stale ledger data from affecting future CF calculations
            try {
              const ledgersQuery = query(
                collection(DATABASE, "ledgers"),
                where("doc_id", "==", Data?.doc_id),
                where("user_id", "==", Data?.user_id)
              );
              const ledgersSnapshot = await getDocs(ledgersQuery);
              if (!ledgersSnapshot.empty) {
                for (const ledgerDoc of ledgersSnapshot.docs) {
                  const ledgerData = ledgerDoc.data();
                  await updateDoc(ledgerDoc.ref, {
                    current_balance: ledgerData.previous_balance,
                    request_status: 4,
                  });
                }
                console.log(`✅ Ledger updated for rejected withdrawal ${Data?.doc_id}`);
              }
            } catch (ledgerError) {
              console.error(`⚠️ Could not update ledger for rejected withdrawal:`, ledgerError);
            }

            console.log(`✅ Admin Panel: Balance restoration transaction completed for rejected withdrawal ${Data?.doc_id}`);
          } else {
            console.log(`⚠️ [REJECTION] Balance restoration skipped: shouldRestore=${shouldRestore}, already_restored=${txnData?.balance_restored}`);
          }
        }
        
        const updateData = {
          request_status: Status,
          request_approval_time: Status === 5 ? serverTimestamp() : Timestamp.now(),
        };
        
        if (Status === 5 && Data?.full_withdraw_paid === 0) {
          updateData.full_withdraw_paid = Data?.is_full_withdraw === true ? 1 : 0;

          const txnRef = doc(DATABASE, "transection_requests", Data?.doc_id);
          const transactionResult = await runTransaction(DATABASE, async (transaction) => {
            const txnSnap = await transaction.get(txnRef);
            if (!txnSnap.exists()) {
              throw new Error("Transaction does not exist");
            }
            const txnData = txnSnap.data();

            if (txnData?.ledger_created === true) {
              return { ledgerAlreadyCreated: true };
            }

            const userRef = doc(DATABASE, "users", Data?.user_id);
            const userSnap = await transaction.get(userRef);
            if (!userSnap.exists()) {
              throw new Error("User does not exist");
            }

            const currentBalance = userSnap?.data()?.net_balance || 0;
            amountToDeduct = Data?.offered_amount
              ? parseFloat(Data?.offered_amount)
              : parseFloat(Data?.total_amount);

            const previousBalance = Data?.original_balance || currentBalance + amountToDeduct;
            const correctCurrentBalance = previousBalance - amountToDeduct;
            const ledgerRef = doc(DATABASE, "ledgers", `withdraw_${Data?.doc_id}`);
            const ledgerSnap = await transaction.get(ledgerRef);

            if (!ledgerSnap.exists()) {
              const transactionDate = Data?.request_approval_time || Data?.date || Timestamp.now();
              const requestDate = Data?.date || Timestamp.now();
              const conclusionDate = Data?.request_approval_time || Timestamp.now();

              const ledgerEntry = {
                new_txn_amount: amountToDeduct,
                user_id: Data?.user_id,
                date: transactionDate,
                request_date: requestDate,
                conclusion_date: conclusionDate,
                previous_balance: previousBalance,
                current_balance: correctCurrentBalance,
                transection_type: 2,
                doc_id: Data?.doc_id,
                request_status: Status,
                profit_loss_percent: 0,
                profit_loss_balance: 0,
              };

              transaction.set(ledgerRef, ledgerEntry);
            }

            transaction.update(txnRef, {
              ledger_created: true,
            });

            return { ledgerAlreadyCreated: false };
          });

          if (!transactionResult?.ledgerAlreadyCreated) {
            updateData.ledger_created = true;
            console.log(`✅ Created ledger entry for withdrawal ${Data?.doc_id} (admin panel)`);
          } else {
            // Ledger already created by Cloud Function - just update the status in existing ledger
            // Find the ledger entry by doc_id
            const ledgersQuery = query(
              collection(DATABASE, "ledgers"),
              where("doc_id", "==", Data?.doc_id),
              where("user_id", "==", Data?.user_id)
            );
            const ledgersSnapshot = await getDocs(ledgersQuery);
            
            if (!ledgersSnapshot.empty) {
              // If multiple ledgers exist (shouldn't happen, but handle it), update all of them
              const updatePromises = ledgersSnapshot.docs.map(async (ledgerDoc) => {
                await updateDoc(ledgerDoc.ref, {
                  request_status: Status, // Update to Status 5 (paid)
                  conclusion_date: Data?.request_approval_time || Timestamp.now(), // Update conclusion date
                });
              });
              await Promise.all(updatePromises);
              console.log(`✅ Updated ${ledgersSnapshot.docs.length} existing ledger entry/entries for withdrawal ${Data?.doc_id} to Status 5`);
            } else {
              console.warn(`⚠️ Ledger marked as created but not found in ledgers collection for ${Data?.doc_id}`);
              // Edge case: Flag says ledger created but not found - create it now
              const userDoc = await getDoc(doc(DATABASE, "users", Data?.user_id));
              if (userDoc.exists()) {
                const currentBalance = userDoc?.data()?.net_balance || 0;
                amountToDeduct = Data?.offered_amount
                  ? parseFloat(Data?.offered_amount)
                  : parseFloat(Data?.total_amount);
                const previousBalance = Data?.original_balance || currentBalance + amountToDeduct;
                const correctCurrentBalance = previousBalance - amountToDeduct;
                const ledgerId = Date.now().toString();
                const ledgerRef = doc(DATABASE, "ledgers", ledgerId);
                const ledgerEntry = {
                  new_txn_amount: amountToDeduct,
                  user_id: Data?.user_id,
                  date: Data?.request_approval_time || Data?.date || Timestamp.now(),
                  request_date: Data?.date || Timestamp.now(),
                  conclusion_date: Data?.request_approval_time || Timestamp.now(),
                  previous_balance: previousBalance,
                  current_balance: correctCurrentBalance,
                  transection_type: 2,
                  doc_id: Data?.doc_id,
                  request_status: Status,
                  profit_loss_percent: 0,
                  profit_loss_balance: 0,
                };
                await setDoc(ledgerRef, ledgerEntry);
                console.log(`✅ Created missing ledger entry for withdrawal ${Data?.doc_id} (edge case)`);
              }
            }
          }
        }
        
        await updateDoc(
          doc(DATABASE, "transection_requests", Data?.doc_id),
          updateData
        );
        getWithdrow(page, pageSize, withdrowStatus);
        const successMessages = {
          2: "Withdraw request approved successfully!",
          4: "Withdraw request rejected.",
          5: "Withdraw request marked as paid.",
        };
        const message =
          successMessages[Status] || "Withdraw status updated successfully!";
        toast.success(message, { autoClose: 3000 });
      } catch (error) {
        console.error("Error updating withdraw status:", error);
        toast.error(error.message || "Something went wrong. Try again!", {
          autoClose: 3000,
        });
    } finally {
      setIsLoading(false);
      setProcessingWithdrawId(null);
    }
  };
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    getWithdrow(0, newPageSize, withdrowStatus);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
    getWithdrow(newPage, pageSize, withdrowStatus);
  };
  const handleAcceptRejectOfferAmount = (params) => {
    setIsFullWithdrawModel(true);
    setOfferAmountDetails(params);
  };
  const handleOfferAmountOnSubmit = () => {
    setIsFullWithdrawModel(false);
    setOfferAmountDetails({});
  };

  const renderBybitHeaderMeta = () => (
    <>
      {bybitStatus.loading && bybitBalanceEntries.length === 0 ? (
        <>
          <Skeleton variant="rounded" width={isMobile ? 88 : 120} height={24} />
          <Skeleton variant="rounded" width={isMobile ? 88 : 120} height={24} />
        </>
      ) : (
        bybitBalanceEntries.map((entry) =>
          renderBybitBalanceChip(entry, { compact: isMobile })
        )
      )}
      {!isMobile && bybitStatus.loading && !bybitStatus.keyCreatedAt ? null : (() => {
        const { daysLeft, keyCreatedAt } = bybitStatus;
        if (!keyCreatedAt) {
          return (
            <Tooltip title="Click to set API key creation date">
              <Chip
                icon={<KeyIcon sx={{ fontSize: 14 }} />}
                label="key date?"
                size="small"
                color="warning"
                variant="outlined"
                onClick={() => setBybitSettingsOpen((v) => !v)}
                sx={{ height: 24, cursor: "pointer" }}
              />
            </Tooltip>
          );
        }
        const expColor = daysLeft <= 15 ? "error" : daysLeft <= 30 ? "warning" : "success";
        const expLabel = daysLeft <= 0 ? "key EXPIRED" : `key ${daysLeft}d`;
        return (
          <Tooltip title={`Bybit API key expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}`}>
            <Chip
              icon={<KeyIcon sx={{ fontSize: 14 }} />}
              label={expLabel}
              size="small"
              color={expColor}
              variant="outlined"
              sx={{ height: 24 }}
            />
          </Tooltip>
        );
      })()}
      {!isMobile && (
        <>
          <Tooltip title="Refresh Bybit info">
            <IconButton size="small" onClick={loadBybitStatus} disabled={bybitStatus.loading} sx={{ p: 0.25 }}>
              {bybitStatus.loading ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title={bybitSettingsOpen ? "Close settings" : "Bybit settings"}>
            <IconButton
              size="small"
              onClick={() => setBybitSettingsOpen((v) => !v)}
              color={bybitSettingsOpen ? "primary" : "default"}
              sx={{ p: 0.25 }}
            >
              <SettingsIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </>
  );

  const renderSystemHealthStrip = () => (
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 0.5 }}>
        <MonitorHeartIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
          System health
        </Typography>
      </Box>
      {health.loading ? (
        <>
          <Skeleton variant="rounded" width={120} height={26} />
          <Skeleton variant="rounded" width={120} height={26} />
          <Skeleton variant="rounded" width={120} height={26} />
        </>
      ) : (
        [
          { key: "failed", label: "Failed — action", count: health.failed, bg: "#D32F2F", Icon: WarningAmberIcon, status: 2, tip: "Auto-pay failed. Needs manual payment or reject." },
          { key: "sending", label: "Sending on Bybit", count: health.sending, bg: "#FF9800", Icon: HourglassEmptyIcon, status: 2, tip: "Accepted by auto-pay and being sent on-chain via Bybit." },
          { key: "awaitingManualPay", label: "Awaiting manual pay", count: health.awaitingManualPay, bg: "#0288D1", Icon: PaidIcon, status: 2, tip: "Accepted withdrawals waiting for manual on-chain payment." },
          { key: "queued", label: "Queued", count: health.queued, bg: "#6A1B9A", Icon: ScheduleIcon, status: 1, tip: "In the auto-pay queue behind another request from the same client." },
          { key: "awaitingApproval", label: "Awaiting approval", count: health.awaitingApproval, bg: "#46adf2", Icon: PendingActionsIcon, status: 1, tip: "Confirmed by client. Waiting for admin to accept or reject." },
          { key: "pendingOtp", label: "OTP pending", count: health.pendingOtp, bg: "orange", Icon: ScheduleIcon, status: 0, tip: "Clients still need to confirm the withdrawal via OTP in the app." },
          { key: "paid24h", label: "Paid (24h)", count: health.paid24h, bg: "#2E7D32", Icon: CheckIcon, status: 5, tip: "Withdrawals paid in the last 24h (auto-pay + manual)." },
        ].map(({ key, label, count, bg, Icon, status, tip }) => {
          const dimmed = count === 0;
          return (
            <Tooltip key={key} title={tip} arrow>
              <Chip
                icon={<Icon sx={{ fontSize: 15, color: "#fff !important" }} />}
                label={`${label}: ${count}`}
                size="small"
                onClick={() => {
                  setWithdrowStatus(status);
                  setPage(0);
                }}
                sx={{
                  height: 26,
                  color: "#fff",
                  fontWeight: 700,
                  backgroundColor: bg,
                  opacity: dimmed ? 0.45 : 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: bg, opacity: dimmed ? 0.6 : 0.9 },
                }}
              />
            </Tooltip>
          );
        })
      )}
    </Box>
  );

  // ========== End Pagination ==========
  return (
    <>
      <Box
        sx={{
          width: "100%",
          bgcolor: "background.default", // Usa o background do tema (dark mode)
          paddingTop:{xs:'60px', sm:"0px"}
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FundsPageShell
              title="Withdraw Funds"
              headerMeta={renderBybitHeaderMeta()}
              totalsProps={{
                totals: periodTotals,
                loading: periodTotalsLoading,
                type: "withdrawal",
                startDate: periodStartDate,
                endDate: periodEndDate,
              }}
              headerExtra={!isMobile ? renderSystemHealthStrip() : null}
              toolbar={
                <FundsPageToolbar
                  searchValue={searchWithdraw}
                  onSearchChange={(event) => setSearchWithdraw(event.target.value)}
                  onSearchClear={() => setSearchWithdraw("")}
                  periodStartDate={periodStartDate}
                  periodEndDate={periodEndDate}
                  onPeriodStartChange={handlePeriodStartChange}
                  onPeriodEndChange={handlePeriodEndChange}
                  resetKey={datePickerResetKey}
                  filterActionsProps={{
                    statusList: withdrowStatusList,
                    statusValue: withdrowStatus,
                    onStatusChange: (e) => {
                      const raw = e.target.value;
                      setWithdrowStatus(raw === "" ? null : raw);
                      setPage(0);
                    },
                    statusPlaceholder: "Select Withdraw",
                    clientSegment,
                    onClientSegmentChange: handleClientSegmentChange,
                    onResetFilters: resetAllFilters,
                    filtersActive,
                  }}
                />
              }
            />
          </Grid>

          {/* Bybit settings panel — desktop only */}
          {bybitSettingsOpen && !isMobile && (
            <Grid item xs={12}>
              <Box
                sx={{
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  p: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                    API Key creation date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={bybitSettingsForm.keyDate}
                    onChange={(e) => setBybitSettingsForm((f) => ({ ...f, keyDate: e.target.value }))}
                    inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                    sx={{ width: 180 }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                    Auto-Pay on-chain (Bybit)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bybitSettingsForm.exchangeEnabled}
                        onChange={(e) =>
                          setBybitSettingsForm((f) => ({ ...f, exchangeEnabled: e.target.checked }))
                        }
                        size="small"
                      />
                    }
                    label={bybitSettingsForm.exchangeEnabled ? "Enabled" : "Disabled"}
                    sx={{ m: 0 }}
                  />
                </Box>

                <Box>
                  <Tooltip title="When ON, Auto-Pay only pays clients with an approved KYC profile (kyc_profiles status = approved). When OFF, behaviour is unchanged.">
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                      Require approved KYC
                    </Typography>
                  </Tooltip>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={bybitSettingsForm.enforceKyc}
                        onChange={(e) =>
                          setBybitSettingsForm((f) => ({ ...f, enforceKyc: e.target.checked }))
                        }
                        size="small"
                      />
                    }
                    label={bybitSettingsForm.enforceKyc ? "Enforced" : "Off"}
                    sx={{ m: 0 }}
                  />
                </Box>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={bybitSettingsSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                  onClick={saveBybitSettings}
                  disabled={bybitSettingsSaving}
                  sx={{ height: 36 }}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          )}

        </Grid>
        <Box
          sx={{
            width: "100%",
            paddingTop: "24px",
          }}
        >
          {/* Desktop: TableContainer (comportamento atual) */}
          {!isMobile && (
            <TableContainer
              rows={withdrowList}
              columns={columns}
              loading={isLoading}
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              handlePageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              paginationShow={true}
              density="standard"
              rowHeight={58}
              hideFillerColumn
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": { fontSize: 15, fontWeight: 700 },
                "& .MuiDataGrid-cell": {
                  fontSize: 14,
                  py: 1,
                  color: "text.primary",
                },
                "& .MuiDataGrid-row": {
                  "&:hover": { bgcolor: "rgba(42, 245, 152, 0.06)" },
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  bgcolor: "rgba(255, 255, 255, 0.02)",
                },
                "& .rightAlign": {
                  justifyContent: "flex-end",
                },
              }}
            />
          )}
          
          {/* Mobile: MobileCardView (nova visualização) */}
          {isMobile && (
            <MobileCardView
              rows={withdrowList}
              loading={isLoading}
              type="withdrawal"
              withdrawalThreshold={withdrawalThreshold}
              onApprove={(row) => {
                // For full withdraw or conditional offer, use handleAcceptRejectOfferAmount
                if (row?.is_full_withdraw) {
                  handleAcceptRejectOfferAmount(row);
                } else {
                  handleAcceptRejectOnChange(row, 2);
                }
              }}
              onReject={(row) => handleAcceptRejectOnChange(row, 4)}
              onPay={(row) => handleAcceptRejectOnChange(row, 5)}
              onConditionalOffer={(row) => handleAcceptRejectOfferAmount(row)}
              processingId={processingWithdrawId}
            />
          )}
        </Box>
      </Box>
      <FullWithdrawModel
        open={isFullWithdrawModel}
        onClose={() => setIsFullWithdrawModel(false)}
        offerAmountDetails={offerAmountDetails}
        handleOfferAmountOnSubmit={handleOfferAmountOnSubmit}
      />
    </> 
  );
};

export default WithdrowFunds;